// services/supabaseService.js
import { supabase } from '../lib/supabase';

// Ground operations
export const groundsService = {
  // Create a new ground
  async create(groundData) {
    // Get the current authenticated user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      throw new Error('User must be authenticated to create a ground');
    }

    const { data, error } = await supabase
      .from('grounds')
      .insert([{
        ground_name: groundData.groundName,
        address_1: groundData.address1,
        address_2: groundData.address2 || null,
        city: groundData.city,
        state: groundData.state,
        zip_code: groundData.zipCode,
        country: groundData.country || 'United States',
        created_by: user.id
      }])
      .select();

    if (error) throw error;
    return data[0];
  },

  // Get all grounds for the current user
  async getAll() {
    const { data, error } = await supabase
      .from('grounds')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Get a single ground by ID
  async getById(id) {
    const { data, error } = await supabase
      .from('grounds')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  // Update a ground
  async update(id, groundData) {
    const { data, error } = await supabase
      .from('grounds')
      .update({
        ground_name: groundData.groundName,
        address_1: groundData.address1,
        address_2: groundData.address2 || null,
        city: groundData.city,
        state: groundData.state,
        zip_code: groundData.zipCode,
        country: groundData.country || 'United States'
      })
      .eq('id', id)
      .select();

    if (error) throw error;
    return data[0];
  },

  // Delete a ground
  async delete(id) {
    const { error } = await supabase
      .from('grounds')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  }
};

// Schedule operations
export const schedulesService = {
  // Generate schedule algorithm with ground assignments and custom team names
  generateSchedule(numTeams, gamesPerTeam, weeksToComplete, groundAssignmentData = null, customTeamNames = null) {
    // Build team names - use custom names if provided, otherwise default
    let teams;
    if (customTeamNames && customTeamNames.length === numTeams) {
      teams = [...customTeamNames];
    } else {
      teams = Array.from({ length: numTeams }, (_, i) => `Team ${i + 1}`);
    }

    // If odd number of teams, add a BYE
    if (teams.length % 2 !== 0) {
      teams.push('BYE');
    }

    const teamCount = teams.length;
    const roundsPerCycle = teamCount - 1;
    const matchesPerRound = teamCount / 2;

    // Build single-cycle rounds using the circle method
    const teamList = [...teams];
    const roundsSingleCycle = [];
    for (let round = 0; round < roundsPerCycle; round++) {
      const roundMatches = [];
      for (let i = 0; i < matchesPerRound; i++) {
        const t1 = teamList[i];
        const t2 = teamList[teamCount - 1 - i];

        // Skip BYE matches
        if (t1 !== 'BYE' && t2 !== 'BYE') {
          roundMatches.push([t1, t2]);
        }
      }
      roundsSingleCycle.push(roundMatches);

      // Rotate: move last element into index 1 (classic circle method)
      const last = teamList.pop();
      teamList.splice(1, 0, last);
    }

    // Flatten to count matches in one cycle
    const matchesPerCycle = roundsSingleCycle.reduce((acc, r) => acc + r.length, 0);

    // How many games total we actually need
    const totalGamesNeeded = Math.floor((numTeams * gamesPerTeam) / 2);
    if (totalGamesNeeded <= 0) return [];

    // How many cycles we need to cover totalGamesNeeded
    const cyclesNeeded = Math.ceil(totalGamesNeeded / matchesPerCycle);

    // Build rounds across cycles, randomizing home/away in each cycle's round copy
    const roundsAll = [];
    for (let cycle = 0; cycle < cyclesNeeded; cycle++) {
      for (let r = 0; r < roundsSingleCycle.length; r++) {
        const thisRound = roundsSingleCycle[r].map(([a, b]) => (Math.random() < 0.5 ? [a, b] : [b, a]));
        roundsAll.push(thisRound);
      }
    }

    // Collect rounds until we've reached totalGamesNeeded
    const selectedRounds = [];
    let gamesCollected = 0;
    for (let ri = 0; ri < roundsAll.length && gamesCollected < totalGamesNeeded; ri++) {
      const roundMatches = roundsAll[ri];
      const remainingNeeded = totalGamesNeeded - gamesCollected;

      if (roundMatches.length <= remainingNeeded) {
        selectedRounds.push([...roundMatches]);
        gamesCollected += roundMatches.length;
      } else {
        selectedRounds.push(roundMatches.slice(0, remainingNeeded));
        gamesCollected += remainingNeeded;
      }
    }

    // Prepare weeks array
    const weeks = Array.from({ length: weeksToComplete }, (_, i) => ({ week: i + 1, games: [] }));

    // Distribute rounds proportionally across weeks
// Distribute games sequentially across weeks
const roundsUsedCount = selectedRounds.length;
let gameIndex = 0;
const gamesPerWeek = Math.ceil(totalGamesNeeded / weeksToComplete);
let currentWeekIdx = 0;
let gamesInCurrentWeek = 0;

for (let ri = 0; ri < roundsUsedCount; ri++) {
  const round = selectedRounds[ri];

  for (let mi = 0; mi < round.length; mi++) {
    // Check if we need to move to next week
    if (gamesInCurrentWeek >= gamesPerWeek && currentWeekIdx < weeksToComplete - 1) {
      currentWeekIdx++;
      gamesInCurrentWeek = 0;
    }

    const [homeTeam, awayTeam] = round[mi];
    const targetWeek = weeks[currentWeekIdx];

    const game = {
      id: `game_${ri}_${mi}`,
      homeTeam,
      awayTeam,
      week: targetWeek.week,
      date: null,
      time: null
    };

    // Assign ground if provided
    if (groundAssignmentData && groundAssignmentData.grounds && groundAssignmentData.grounds.length > 0) {
      const assignedGround = this.assignGroundToGame(gameIndex, groundAssignmentData);
      if (assignedGround) {
        game.ground_id = assignedGround.id;
        game.ground_name = assignedGround.ground_name;
        game.ground_city = assignedGround.city;
        game.ground_state = assignedGround.state;
        game.ground_address_1 = assignedGround.address_1;
        game.ground_address_2 = assignedGround.address_2;
        game.ground_zip_code = assignedGround.zip_code;
        game.ground_country = assignedGround.country;
      }
    }

    targetWeek.games.push(game);
    gameIndex++;
    gamesInCurrentWeek++;
  }
}

    return weeks.filter(w => w.games.length > 0);
  },

  // Helper method to assign ground to individual game
  assignGroundToGame(gameIndex, groundAssignmentData) {
    const { grounds } = groundAssignmentData || {};
    if (!grounds || grounds.length === 0) return null;
    return grounds[gameIndex % grounds.length];
  },

  // Extract team names from existing schedule data
  extractTeamNames(scheduleData) {
    const teamNamesSet = new Set();
    
    if (scheduleData.schedule_data && scheduleData.schedule_data.length > 0) {
      scheduleData.schedule_data.forEach(week => {
        week.games.forEach(game => {
          if (game.homeTeam && game.homeTeam !== 'BYE') {
            teamNamesSet.add(game.homeTeam);
          }
          if (game.awayTeam && game.awayTeam !== 'BYE') {
            teamNamesSet.add(game.awayTeam);
          }
        });
      });
    }
    
    return Array.from(teamNamesSet).sort();
  },

  // Update team names in existing schedule
  updateTeamNamesInSchedule(scheduleData, teamNameMapping) {
    if (!scheduleData || !scheduleData.length || !teamNameMapping) {
      return scheduleData;
    }

    return scheduleData.map(week => ({
      ...week,
      games: week.games.map(game => ({
        ...game,
        homeTeam: teamNameMapping[game.homeTeam] || game.homeTeam,
        awayTeam: teamNameMapping[game.awayTeam] || game.awayTeam
      }))
    }));
  },

  // Create a new schedule with ground assignments
  async create(scheduleData) {
    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError || !user) {
      throw new Error('User must be authenticated to create a schedule');
    }

    // Prepare ground assignment data if provided
    let groundAssignmentData = null;
    if (scheduleData.assigned_grounds && scheduleData.assigned_grounds.length > 0) {
      const { data: groundsData, error: groundsError } = await supabase
        .from('grounds')
        .select('*')
        .in('id', scheduleData.assigned_grounds);

      if (groundsError) {
        console.error('Error fetching grounds:', groundsError);
      } else {
        groundAssignmentData = { grounds: groundsData };
      }
    }

    // Generate schedule with ground assignments and custom team names
    const generatedSchedule = this.generateSchedule(
      scheduleData.numberOfTeams,
      scheduleData.gamesPerTeam,
      scheduleData.weeksToComplete,
      groundAssignmentData,
      scheduleData.teamNames
    );

    const { data, error } = await supabase
      .from('schedules')
      .insert([{
        number_of_teams: scheduleData.numberOfTeams,
        games_per_team: scheduleData.gamesPerTeam,
        weeks_to_complete: scheduleData.weeksToComplete,
        schedule_data: generatedSchedule,
        assigned_grounds: scheduleData.assigned_grounds || [],
        team_names: scheduleData.teamNames || [],
        created_by: user.id
      }])
      .select();

    if (error) throw error;
    return data[0];
  },

  // Get all schedules for the current user
  async getAll() {
    const { data, error } = await supabase
      .from('schedules')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Get a single schedule by ID
  async getById(id) {
    const { data, error } = await supabase
      .from('schedules')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  // Update schedule with enhanced functionality
  // Replace your existing update method in schedulesService with this complete version

async update(id, updateData) {
  try {
    console.log('Update called with:', { id, updateData });

    // Handle lightweight edits from SeparatedScheduleEditor
// Handle lightweight edits from SeparatedScheduleEditor
    if (updateData.gameEdit || updateData.venueAssignments || 
        updateData.dateScheduling || updateData.bulkOperation) {
      
      console.log('Performing lightweight schedule data update');
      
      // Direct schedule_data update - no regeneration needed
      const { error } = await supabase
        .from('schedules')
        .update({
          schedule_data: updateData.scheduleData,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      // CRITICAL: Re-fetch to ensure we get the committed data
      console.log('🔄 Re-fetching to ensure data consistency...');
      const { data: refetchedData, error: refetchError } = await supabase
        .from('schedules')
        .select('*')
        .eq('id', id)
        .single();

      if (refetchError) {
        console.error('Refetch error:', refetchError);
        throw refetchError;
      }

      console.log('✅ Schedule updated and re-fetched successfully:', refetchedData);
      return refetchedData;
    }

    // Handle team name updates from SeparatedScheduleEditor
    if (updateData.teamNamesOnly && updateData.teamNameMapping) {
      console.log('Performing team name update');
      
      const existingSchedule = await this.getById(id);
      const updatedScheduleData = this.updateTeamNamesInSchedule(
        existingSchedule.schedule_data,
        updateData.teamNameMapping
      );

      const { data, error } = await supabase
        .from('schedules')
        .update({
          schedule_data: updatedScheduleData,
          team_names: updateData.teamNames || existingSchedule.team_names,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Team names updated successfully:', data[0]);
      return data[0];
    }

    // Handle full schedule regeneration (your existing logic)
    console.log('Performing full schedule regeneration');
    
    const existingSchedule = await this.getById(id);
    
    // Prepare ground assignment data if provided
    let groundAssignmentData = null;
    if (updateData.assigned_grounds && updateData.assigned_grounds.length > 0) {
      const { data: groundsData, error: groundsError } = await supabase
        .from('grounds')
        .select('*')
        .in('id', updateData.assigned_grounds);

      if (!groundsError) {
        groundAssignmentData = { grounds: groundsData };
      }
    }

    let updatedScheduleData;
    let finalTeamNames = updateData.teamNames || [];

    // Full regeneration with new parameters
    updatedScheduleData = this.generateSchedule(
      updateData.numberOfTeams || existingSchedule.number_of_teams,
      updateData.gamesPerTeam || existingSchedule.games_per_team,
      updateData.weeksToComplete || existingSchedule.weeks_to_complete,
      groundAssignmentData,
      updateData.teamNames
    );

    // Prepare update object
    const updateObj = {
      schedule_data: updatedScheduleData,
      updated_at: new Date().toISOString()
    };

    // Only update fields that were provided
    if (updateData.numberOfTeams !== undefined) {
      updateObj.number_of_teams = updateData.numberOfTeams;
    }
    if (updateData.gamesPerTeam !== undefined) {
      updateObj.games_per_team = updateData.gamesPerTeam;
    }
    if (updateData.weeksToComplete !== undefined) {
      updateObj.weeks_to_complete = updateData.weeksToComplete;
    }
    if (updateData.assigned_grounds !== undefined) {
      updateObj.assigned_grounds = updateData.assigned_grounds;
    }
    if (finalTeamNames.length > 0) {
      updateObj.team_names = finalTeamNames;
    }

    const { data, error } = await supabase
      .from('schedules')
      .update(updateObj)
      .eq('id', id)
      .select();

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    console.log('Full schedule regeneration completed:', data[0]);
    return data[0];

  } catch (err) {
    console.error('Error in schedulesService.update:', err);
    throw err;
  }
},

  // Update only team names (lightweight update)
  async updateTeamNames(id, teamNameMapping) {
    return this.update(id, {
      teamNamesOnly: true,
      teamNameMapping: teamNameMapping
    });
  },

  // Delete a schedule
  async delete(id) {
    const { error } = await supabase
      .from('schedules')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  }
};
